import type { MDXComponents } from 'mdx/types';
import type { ComponentType } from 'react';
import { z } from 'zod';

/** An h2/h3 of a guide, exported by the MDX pipeline (`vite.mdx.ts`) for "On this page". */
export interface GuideHeading {
  depth: 2 | 3;
  id: string;
  text: string;
}

export interface Guide {
  slug: string;
  title: string;
  description: string;
  order: number;
  Component: ComponentType<{ components?: MDXComponents }>;
  headings: GuideHeading[];
}

/** The landing guide lives at `/docs` itself rather than under `/docs/guides`. */
export const GETTING_STARTED_SLUG = 'getting-started';

const frontmatterSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  order: z.number().int(),
});

interface GuideModule {
  default: Guide['Component'];
  frontmatter: unknown;
  headings: GuideHeading[];
}

const modules = import.meta.glob<GuideModule>('./*.mdx', { eager: true });

function toGuide(path: string, module: GuideModule): Guide {
  const slug = path.replace(/^\.\//, '').replace(/\.mdx$/, '');
  const parsed = frontmatterSchema.safeParse(module.frontmatter);
  if (!parsed.success) {
    throw new Error(`Guide ${slug}.mdx has invalid frontmatter: ${parsed.error.message}`);
  }
  return { slug, ...parsed.data, Component: module.default, headings: module.headings };
}

export const guides: Guide[] = Object.entries(modules)
  .map(([path, module]) => toGuide(path, module))
  .sort((a, b) => a.order - b.order);

export function findGuide(slug: string): Guide | undefined {
  return guides.find((guide) => guide.slug === slug);
}
